import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInvoice, useMarkInvoicePaid, useCancelInvoice } from '@/hooks/queries/useInvoices';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ArrowLeft, 
  Download, 
  Share2, 
  Printer,
  Mail,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  DollarSign,
  ChevronDown,
  Languages
} from 'lucide-react';
import { generateInvoicePDF, type InvoiceLanguage } from '@/lib/pdfGeneratorJsPDF';
import { toast } from '@/components/ui/toast/useToast';
import { RecordPaymentModal } from '@/features/invoices/RecordPaymentModal';
import { useInvoicePayments } from '@/hooks/queries/usePayments';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/invoices/$invoiceId/')({
  component: InvoiceViewPage,
});

// Translation helper
const translations = {
  ar: {
    back: 'العودة',
    recordPayment: 'تسجيل دفعة',
    markPaid: 'تعليم مدفوعة',
    cancel: 'إلغاء',
    invoice: 'فاتورة',
    invoiceNumber: 'الرقم',
    date: 'التاريخ',
    dueDate: 'تاريخ الاستحقاق',
    client: 'العميل',
    product: 'المنتج',
    quantity: 'الكمية',
    price: 'السعر',
    vat: 'الضريبة',
    discount: 'الخصم',
    total: 'المجموع',
    subtotal: 'المجموع الفرعي',
    stampDuty: 'الطابع الجبائي',
    totalTTC: 'المجموع الكلي',
    paid: 'المدفوع',
    remaining: 'المتبقي',
    notes: 'ملاحظات',
    paymentTerms: 'شروط الدفع',
    statusLabels: {
      draft: 'مسودة',
      sent: 'مرسلة',
      partial: 'دفع جزئي',
      paid: 'مدفوعة',
      overdue: 'متأخرة',
      cancelled: 'ملغاة',
    }
  },
  fr: {
    back: 'Retour',
    recordPayment: 'Enregistrer un paiement',
    markPaid: 'Marquer comme payée',
    cancel: 'Annuler',
    invoice: 'FACTURE',
    invoiceNumber: 'N°',
    date: 'Date',
    dueDate: 'Échéance',
    client: 'Client',
    product: 'Produit',
    quantity: 'Qté',
    price: 'Prix',
    vat: 'TVA',
    discount: 'Remise',
    total: 'Total',
    subtotal: 'Sous-total HT',
    stampDuty: 'Timbre fiscal',
    totalTTC: 'Total TTC',
    paid: 'Payé',
    remaining: 'Reste à payer',
    notes: 'Notes',
    paymentTerms: 'Conditions de paiement',
    statusLabels: {
      draft: 'Brouillon',
      sent: 'Envoyée',
      partial: 'Partiel',
      paid: 'Payée',
      overdue: 'En retard',
      cancelled: 'Annulée',
    }
  }
};

function InvoiceViewPage() {
  const { invoiceId } = Route.useParams();
  const navigate = useNavigate();
  const { data: invoice, isLoading, error } = useInvoice(invoiceId);
  const { data: payments = [] } = useInvoicePayments(invoiceId);
  const markPaid = useMarkInvoicePaid();
  const cancelInvoice = useCancelInvoice();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [language, setLanguage] = useState<InvoiceLanguage>('fr');
  
  const t = translations[language];
  const isRTL = language === 'ar';

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDownloadPDF = async (pdfLanguage: InvoiceLanguage) => {
    if (!invoice) return;
    
    try {
      await generateInvoicePDF(invoice, pdfLanguage);
      toast.success(
        language === 'ar' ? 'تم التنزيل' : 'Téléchargé',
        `PDF ${pdfLanguage === 'ar' ? 'بالعربية' : 'en français'} téléchargé avec succès`
      );
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(
        language === 'ar' ? 'خطأ' : 'Erreur',
        language === 'ar' ? 'فشل إنشاء PDF' : 'Échec de la génération du PDF'
      );
    }
  };

  const handleEdit = () => {
    navigate({ to: '/draft-invoice', search: { id: invoiceId } });
  };

  const handleMarkPaid = async () => {
    try {
      await markPaid.mutateAsync(invoiceId);
      toast.success(
        language === 'ar' ? 'تم' : 'Marqué',
        language === 'ar' ? 'تم تعليمها كمدفوعة' : 'Marquée comme payée'
      );
    } catch (error) {
      toast.error(language === 'ar' ? 'خطأ' : 'Erreur', language === 'ar' ? 'فشل التحديث' : 'Échec de la mise à jour');
    }
  };

  const handleCancel = async () => {
    if (!confirm(language === 'ar' ? 'هل تريد إلغاء الفاتورة؟' : 'Annuler la facture?')) return;
    try {
      await cancelInvoice.mutateAsync(invoiceId);
      toast.success(
        language === 'ar' ? 'تم الإلغاء' : 'Annulée',
        language === 'ar' ? 'تم إلغاء الفاتورة' : 'Facture annulée'
      );
    } catch (error) {
      toast.error(language === 'ar' ? 'خطأ' : 'Erreur', language === 'ar' ? 'فشل الإلغاء' : 'Échec de l\'annulation');
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !invoice) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96">
          <AlertCircle className="w-16 h-16 text-red-600 mb-4" />
          <h2 className="text-xl font-bold">
            {language === 'ar' ? 'فاتورة غير موجودة' : 'Facture introuvable'}
          </h2>
          <Button 
            className="mt-4"
            onClick={() => navigate({ to: '/apps/sales' })}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const statusColors = {
    draft: 'bg-orange-100 text-orange-800',
    sent: 'bg-blue-100 text-blue-800',
    partial: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    overdue: 'bg-red-100 text-red-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  return (
    <DashboardLayout>
      <div className={cn("p-6 space-y-6 max-w-5xl mx-auto", isRTL && "rtl")} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header Actions */}
        <div className={cn("flex items-center justify-between", isRTL && "flex-row-reverse")}>
          <Button
            variant="ghost"
            onClick={() => navigate({ to: '/apps/sales' })}
          >
            <ArrowLeft className={cn("w-4 h-4", isRTL ? "ml-2" : "mr-2")} />
            {t.back}
          </Button>
          
          <div className={cn("flex flex-wrap gap-2", isRTL && "flex-row-reverse")}>
            {/* Language Toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Languages className="w-4 h-4 mr-2" />
                  {language === 'ar' ? 'العربية' : 'Français'}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLanguage('fr')}>
                  <span className="mr-2">🇫🇷</span>
                  Français
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('ar')}>
                  <span className="mr-2">🇩🇿</span>
                  العربية
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex gap-2">
              {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                <Button
                  onClick={() => setPaymentModalOpen(true)}
                  className="bg-green-600 hover:bg-green-700"
                  size="sm"
                >
                  <DollarSign className={cn("w-4 h-4", isRTL ? "ml-2" : "mr-2")} />
                  {t.recordPayment}
                </Button>
              )}
              
              {invoice.status === 'draft' && (
                <Button
                  onClick={handleMarkPaid}
                  className="bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  <CheckCircle className={cn("w-4 h-4", isRTL ? "ml-2" : "mr-2")} />
                  {t.markPaid}
                </Button>
              )}
              
              {invoice.status !== 'cancelled' && (
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="border-red-600 text-red-600 hover:bg-red-50"
                  size="sm"
                >
                  <XCircle className={cn("w-4 h-4", isRTL ? "ml-2" : "mr-2")} />
                  {t.cancel}
                </Button>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Mail className="w-4 h-4" />
              </Button>
              
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4" />
              </Button>
              
              {/* PDF Download with Language Selection */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleDownloadPDF('fr')}>
                    <span className="mr-2">🇫🇷</span>
                    En Français
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleDownloadPDF('ar')}>
                    <span className="mr-2">🇩🇿</span>
                    بالعربية
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button variant="outline" size="sm">
                <Printer className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Invoice Card */}
        <Card>
          <CardContent className="p-8">
            {/* Header Section */}
            <div className={cn("flex justify-between items-start mb-8", isRTL && "flex-row-reverse")}>
              <div className={isRTL ? "text-right" : ""}>
                <h1 className="text-4xl font-bold mb-2">
                  {t.invoice}
                </h1>
                <div className="space-y-1">
                  <p className="text-lg">
                    <span className="text-gray-500">{t.invoiceNumber}:</span>{' '}
                    <span className="font-bold">{invoice.invoice_number}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    <span>{t.date}:</span>{' '}
                    {formatDate(invoice.created_at)}
                  </p>
                  {invoice.due_date && (
                    <p className="text-sm text-gray-600">
                      <span>{t.dueDate}:</span>{' '}
                      {formatDate(invoice.due_date)}
                    </p>
                  )}
                </div>
              </div>
              
              <div className={isRTL ? "text-left" : "text-right"}>
                <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${statusColors[invoice.status as keyof typeof statusColors]}`}>
                  {t.statusLabels[invoice.status as keyof typeof t.statusLabels]}
                </span>
              </div>
            </div>

            {/* Client Info */}
            <div className="mb-8 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h3 className={cn("font-semibold mb-3", isRTL && "text-right")}>{t.client}</h3>
              <div className={cn("space-y-1", isRTL && "text-right")}>
                <p className="font-bold text-lg">{invoice.clients?.name}</p>
                {invoice.clients?.email && (
                  <p className="text-sm text-gray-600">{invoice.clients.email}</p>
                )}
                {invoice.clients?.phone && (
                  <p className="text-sm text-gray-600">{invoice.clients.phone}</p>
                )}
                {invoice.clients?.address && (
                  <p className="text-sm text-gray-600">{invoice.clients.address}</p>
                )}
                {invoice.clients?.tax_id && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">NIF:</span> {invoice.clients.tax_id}
                  </p>
                )}
                {invoice.clients?.nrc && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">RC:</span> {invoice.clients.nrc}
                  </p>
                )}
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-8 overflow-x-auto">
              <table className="w-full">
                <thead className="border-b-2 border-gray-300">
                  <tr>
                    <th className={cn("p-3 font-semibold", isRTL ? "text-right" : "text-left")}>
                      {t.product}
                    </th>
                    <th className="text-center p-3 font-semibold w-24">
                      {t.quantity}
                    </th>
                    <th className="text-right p-3 font-semibold w-32">
                      {t.price}
                    </th>
                    <th className="text-center p-3 font-semibold w-24">
                      {t.vat}
                    </th>
                    {invoice.invoice_items?.some((item: any) => item.discount > 0) && (
                      <th className="text-right p-3 font-semibold w-32">
                        {t.discount}
                      </th>
                    )}
                    <th className="text-right p-3 font-semibold w-32">
                      {t.total}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invoice.invoice_items?.map((item: any) => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className={cn("p-3", isRTL ? "text-right" : "text-left")}>
                        <div>
                          <p className="font-medium">{item.products?.name}</p>
                          {item.description && (
                            <p className="text-sm text-gray-500">{item.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="text-center p-3">
                        {item.quantity} {item.unit}
                      </td>
                      <td className="text-right p-3">
                        {formatCurrency(item.unit_price)}
                      </td>
                      <td className="text-center p-3">
                        {item.vat}%
                      </td>
                      {invoice.invoice_items?.some((i: any) => i.discount > 0) && (
                        <td className="text-right p-3">
                          {item.discount > 0 ? (
                            <span className="text-red-600">
                              -{item.discount_type === 'percentage' 
                                ? `${item.discount}%` 
                                : formatCurrency(item.discount)}
                            </span>
                          ) : '-'}
                        </td>
                      )}
                      <td className="text-right p-3 font-medium">
                        {formatCurrency(item.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="mb-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <h4 className={cn("font-semibold mb-2", isRTL && "text-right")}>{t.notes}</h4>
                <p className={cn("text-sm", isRTL && "text-right")}>{invoice.notes}</p>
              </div>
            )}

            {/* Totals */}
            <div className={cn("flex", isRTL ? "justify-start" : "justify-end")}>
              <div className="w-80 space-y-3">
                <div className={cn("flex justify-between text-gray-600", isRTL && "flex-row-reverse")}>
                  <span>{t.subtotal}:</span>
                  <span className="font-medium">{formatCurrency(invoice.subtotal || 0)}</span>
                </div>
                
                {invoice.discount && invoice.discount > 0 && (
                  <div className={cn("flex justify-between text-red-600", isRTL && "flex-row-reverse")}>
                    <span>{t.discount}:</span>
                    <span className="font-medium">-{formatCurrency(invoice.discount)}</span>
                  </div>
                )}
                
                <div className={cn("flex justify-between text-gray-600", isRTL && "flex-row-reverse")}>
                  <span>{t.vat}:</span>
                  <span className="font-medium">{formatCurrency(invoice.tax_amount || 0)}</span>
                </div>
                
                {invoice.stamp_duty && invoice.stamp_duty > 0 && (
                  <div className={cn("flex justify-between text-gray-600", isRTL && "flex-row-reverse")}>
                    <span>{t.stampDuty}:</span>
                    <span className="font-medium">{formatCurrency(invoice.stamp_duty)}</span>
                  </div>
                )}
                
                <div className={cn("flex justify-between text-xl font-bold border-t-2 pt-3", isRTL && "flex-row-reverse")}>
                  <span>{t.totalTTC}:</span>
                  <span className="text-emerald-600">{formatCurrency(invoice.total || 0)}</span>
                </div>
                
                {invoice.paid_amount && invoice.paid_amount > 0 && (
                  <>
                    <div className={cn("flex justify-between text-gray-600", isRTL && "flex-row-reverse")}>
                      <span>{t.paid}:</span>
                      <span className="font-medium text-green-600">
                        {formatCurrency(invoice.paid_amount)}
                      </span>
                    </div>
                    <div className={cn("flex justify-between text-lg font-bold", isRTL && "flex-row-reverse")}>
                      <span>{t.remaining}:</span>
                      <span className={invoice.total - invoice.paid_amount > 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatCurrency(invoice.total - invoice.paid_amount)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Payment Terms */}
            {invoice.payment_terms && (
              <div className="mt-8 pt-4 border-t">
                <p className={cn("text-sm text-gray-600", isRTL && "text-right")}>
                  <span className="font-medium">{t.paymentTerms}:</span>{' '}
                  {invoice.payment_terms}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Payment History */}
        {payments.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">Historique des paiements</h3>
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div key={payment.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{formatDate(payment.payment_date)}</p>
                      <p className="text-sm text-gray-600">
                        {payment.payment_method === 'check' && payment.check_number && (
                          <span>Chèque #{payment.check_number} - </span>
                        )}
                        {payment.transaction?.transaction_number}
                      </p>
                    </div>
                    <p className="text-lg font-bold text-green-600">
                      {formatCurrency(payment.amount_allocated)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Payment Modal */}
      {invoice && (
        <RecordPaymentModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          invoice={{
            id: invoice.id,
            invoice_number: invoice.invoice_number,
            client_name: invoice.clients?.name || '',
            total: invoice.total || 0,
            amount_paid: invoice.amount_paid || 0,
            amount_outstanding: invoice.amount_outstanding || invoice.total || 0,
          }}
        />
      )}
    </DashboardLayout>
  );
}
